import { IFormPageData } from '../lib/models/form-page-data';
import { RuntimeContext } from '../lib/models/element-types';

export interface FormPageInfo {
    id: string;
    name?: string;
    data: IFormPageData;
}

export interface FormData {
    formId?: string;
    pageData: IFormPageData;
    pages?: FormPageInfo[];
    thankYouPage?: IFormPageData;
    settings?: FormSettings;
    runtimeContext?: RuntimeContext;
    isDraft?: boolean;
    optimizedGoogleFontsLink: string;
}

export type SubmitActionType = 'ThankYouPage' | 'Redirect' | 'None';

export type FormNavigationType = 'Tabs' | 'ProgressBar' | 'OnlyButtons';

export type FormNavigationPosition = 'Top' | 'Right' | 'Bottom' | 'Left';

export interface FormSettings {
    title?: string;
    language?: string;
    submitUrl?: string;
    submitAction?: SubmitActionType;
    redirectUrl?: string;
    category?: string;
    customCodeHtml?: string;
    navigationType?: FormNavigationType;
    navigationPosition?: FormNavigationPosition;
}

export class FormLoaderService {
    private cache = new Map<string, FormData>();

    public async loadForm(formId: string, apiUrl: string = ''): Promise<FormData> {
        // Check cache first
        const cacheKey = `${apiUrl}:${formId}`;
        const cached = this.cache.get(cacheKey);
        if (cached) {
            return cached;
        }

        const baseUrl = apiUrl || this.getDefaultApiUrl();
        const url = `${baseUrl}/api/public/embed/forms/${formId}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.code || `HTTP ${response.status}`);
        }

        const data = await response.json();

        const formData: FormData = {
            formId: data.formId || formId,
            pageData: data.pageData || (data.sections ? { sections: data.sections } : data),
            pages: data.pages?.map((p: any) => ({
                id: p.id,
                name: p.name,
                data: p.data
            })),
            thankYouPage: data.thankYouPage,
            settings: data.settings,
            runtimeContext: data.runtimeContext,
            isDraft: data.isDraft,
            optimizedGoogleFontsLink: data.optimizedGoogleFontsLink
        };

        // Cache the result
        this.cache.set(cacheKey, formData);

        return formData;
    }

    public clearCache(formId?: string): void {
        if (formId) {
            // Clear specific form from cache
            for (const key of this.cache.keys()) {
                if (key.endsWith(`:${formId}`)) {
                    this.cache.delete(key);
                }
            }
        } else {
            // Clear all cache
            this.cache.clear();
        }
    }

    private getDefaultApiUrl(): string {
        // Try to detect API URL from script src
        const scripts = Array.from(document.querySelectorAll('script[src*="tru-forms-embed"]'));
        for (let i = 0; i < scripts.length; i++) {
            const src = scripts[i].getAttribute('src');
            if (src) {
                try {
                    const url = new URL(src, window.location.href);
                    return `${url.protocol}//${url.host}`;
                } catch {
                    // Ignore invalid URLs
                }
            }
        }

        // Fallback to current origin
        return window.location.origin;
    }
}
